import type { TFunction } from "i18next";
import { z } from "zod";
import type { ConnectorCreate, ConnectorEndpoint, ConnectorResponse, ConnectorUpdate } from "@/api";
import { schemas } from "@/api";
import { checkHeaders, checkSettings } from "./connectorChecks";

export type Transport = "mcp" | "http";
export type AuthMode = "none" | "shared_secret" | "per_user" | "oauth";
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export const TRANSPORTS: Transport[] = ["mcp", "http"];
export const AUTH_MODES: AuthMode[] = ["none", "shared_secret", "per_user", "oauth"];
export const METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];
export const TIMEOUTS = ["15", "30", "60", "120"];

const TOOL_NAME = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;

// Rules from the GENERATED schemas (backend ConnectorCreate/ConnectorEndpoint); the
// per-transport rules mirror the backend's validate_endpoint_for.
const create = schemas.zConnectorCreate.shape;
const endpoint = schemas.zConnectorEndpoint.shape;

/** One declared setting as edited in the form (empty strings = unset). */
export interface SettingRow {
  /** Client-only row identity (React key); never sent. */
  key: string;
  name: string;
  label: string;
  default: string;
  header: string;
  required: boolean;
}

/** One fixed header as edited in the form. */
export interface HeaderRow {
  /** Client-only row identity (React key); never sent. */
  key: string;
  name: string;
  value: string;
}

/** The flat form values (JSON fields are edited as text). */
export interface ConnectorFormValues {
  name: string;
  description: string;
  transport: Transport;
  authMode: AuthMode;
  url: string;
  authHeader: string;
  authScheme: string;
  timeout: string;
  method: HttpMethod;
  toolName: string;
  toolDescription: string;
  parameters: string;
  bodyTemplate: string;
  responsePath: string;
  settings: SettingRow[];
  headers: HeaderRow[];
  /** Sign-in (authMode oauth): the OAuth app's client id, scopes (space-separated) and, optionally, its URLs. */
  oauthClientId: string;
  oauthScopes: string;
  oauthAuthorizeUrl: string;
  oauthTokenUrl: string;
  /** Found from the server's metadata; kept as is. */
  oauthResource: string;
}

const EMPTY_SCHEMA = '{\n  "type": "object",\n  "properties": {}\n}';

function parseObject(text: string): Record<string, unknown> | null | undefined {
  if (!text.trim()) return undefined;
  try {
    const value: unknown = JSON.parse(text);
    return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function connectorFormSchema(t: TFunction<"connectors">) {
  return z
    .object({
      name: create.name,
      description: z.string().max(5000),
      transport: z.enum(["mcp", "http"]),
      authMode: z.enum(["none", "shared_secret", "per_user", "oauth"]),
      oauthClientId: z.string().max(500),
      oauthScopes: z.string().max(5000),
      oauthAuthorizeUrl: z.string().max(2048),
      oauthTokenUrl: z.string().max(2048),
      oauthResource: z.string(),
      url: endpoint.url,
      authHeader: z.string().max(100),
      authScheme: endpoint.authScheme.unwrap().unwrap(),
      timeout: z.string(),
      method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]),
      toolName: z.string(),
      toolDescription: z.string().max(2000),
      parameters: z.string(),
      bodyTemplate: z.string(),
      responsePath: z.string().max(255),
      headers: z.array(z.object({ key: z.string(), name: z.string(), value: z.string() })),
      settings: z.array(
        z.object({
          key: z.string(),
          name: z.string(),
          label: z.string(),
          default: z.string(),
          header: z.string(),
          required: z.boolean(),
        }),
      ),
    })
    .superRefine((v, ctx) => {
      if (!/^https?:\/\//i.test(v.url.trim())) {
        ctx.addIssue({ code: "custom", path: ["url"], message: t("form.errors.url") });
      }
      if (v.authMode === "oauth") checkOAuth(v, ctx, t);
      checkHeaders(v.headers, ctx, t);
      checkSettings(v.settings, ctx, t);
      if (v.transport === "http") checkHttpTool(v, ctx, t);
    });
}

export type Ctx = z.RefinementCtx;
export type T = TFunction<"connectors">;

function checkOAuth(v: ConnectorFormValues, ctx: Ctx, t: T) {
  for (const key of ["oauthAuthorizeUrl", "oauthTokenUrl"] as const) {
    if (v[key].trim() && !/^https:\/\//i.test(v[key].trim())) {
      ctx.addIssue({ code: "custom", path: [key], message: t("form.oauth.errors.https") });
    }
  }
}

function checkHttpTool(v: ConnectorFormValues, ctx: Ctx, t: T) {
  if (!TOOL_NAME.test(v.toolName)) {
    ctx.addIssue({ code: "custom", path: ["toolName"], message: t("form.errors.toolName") });
  }
  if (!v.toolDescription.trim()) {
    ctx.addIssue({ code: "custom", path: ["toolDescription"], message: t("form.errors.toolDescription") });
  }
  const params = parseObject(v.parameters);
  if (params === null) ctx.addIssue({ code: "custom", path: ["parameters"], message: t("form.errors.json") });
  else if (params && params.type !== "object") {
    ctx.addIssue({ code: "custom", path: ["parameters"], message: t("form.errors.schema") });
  }
  if (parseObject(v.bodyTemplate) === null) {
    ctx.addIssue({ code: "custom", path: ["bodyTemplate"], message: t("form.errors.json") });
  }
}

/** Form values for a new connector, or an existing one's settings. */
export function toValues(connector?: ConnectorResponse): ConnectorFormValues {
  const ep = (connector?.endpoint ?? {}) as Partial<ConnectorEndpoint>;
  const json = (value: unknown) => (value ? JSON.stringify(value, null, 2) : "");
  const oauth = ep.oauth;
  return {
    name: connector?.name ?? "",
    description: connector?.description ?? "",
    transport: (connector?.transport as Transport) ?? "mcp",
    authMode: (connector?.authMode as AuthMode) ?? "none",
    url: ep.url ?? "",
    authHeader: ep.authHeader ?? "Authorization",
    authScheme: ep.authScheme ?? "Bearer",
    timeout: String(ep.timeout ?? 60),
    method: (ep.method as HttpMethod) ?? "GET",
    toolName: ep.toolName ?? "",
    toolDescription: ep.toolDescription ?? "",
    parameters: json(ep.parameters) || EMPTY_SCHEMA,
    bodyTemplate: json(ep.bodyTemplate),
    responsePath: ep.responsePath ?? "",
    headers: Object.entries(ep.headers ?? {}).map(([name, value]) => ({ key: crypto.randomUUID(), name, value })),
    settings: (ep.settings ?? []).map((s) => ({
      key: crypto.randomUUID(),
      name: s.name,
      label: s.label,
      default: s.default ?? "",
      header: s.header ?? "",
      required: Boolean(s.required),
    })),
    oauthClientId: oauth?.clientId ?? "",
    oauthScopes: (oauth?.scopes ?? []).join(" "),
    oauthAuthorizeUrl: oauth?.authorizeUrl ?? "",
    oauthTokenUrl: oauth?.tokenUrl ?? "",
    oauthResource: oauth?.resource ?? "",
  };
}

function toOAuth(v: ConnectorFormValues): ConnectorEndpoint["oauth"] {
  if (v.authMode !== "oauth") return null;
  return {
    clientId: v.oauthClientId.trim(),
    scopes: v.oauthScopes.split(/[\s,]+/).filter(Boolean),
    authorizeUrl: v.oauthAuthorizeUrl.trim() || null,
    tokenUrl: v.oauthTokenUrl.trim() || null,
    resource: v.oauthResource.trim() || null,
  };
}

function toSettings(rows: SettingRow[]): ConnectorEndpoint["settings"] {
  if (rows.length === 0) return null;
  return rows.map((r) => ({
    name: r.name.trim(),
    label: r.label.trim(),
    default: r.default.trim() || null,
    header: r.header.trim() || null,
    required: r.required,
  }));
}

function toEndpoint(v: ConnectorFormValues): ConnectorEndpoint {
  const base: ConnectorEndpoint = {
    url: v.url.trim(),
    timeout: Number(v.timeout),
    authHeader: v.authHeader.trim() || "Authorization",
    authScheme: v.authScheme.trim(),
    settings: toSettings(v.settings),
    headers: v.headers.length ? Object.fromEntries(v.headers.map((h) => [h.name.trim(), h.value.trim()])) : null,
    ...(v.authMode === "oauth" ? { oauth: toOAuth(v) } : {}),
  };
  if (v.transport !== "http") return base;
  return {
    ...base,
    method: v.method,
    toolName: v.toolName.trim(),
    toolDescription: v.toolDescription.trim(),
    parameters: parseObject(v.parameters) ?? { type: "object", properties: {} },
    bodyTemplate: v.method === "GET" || v.method === "DELETE" ? null : (parseObject(v.bodyTemplate) ?? null),
    responsePath: v.responsePath.trim() || null,
  };
}

export function toCreateBody(v: ConnectorFormValues): ConnectorCreate {
  return {
    name: v.name.trim(),
    description: v.description.trim() || null,
    transport: v.transport,
    authMode: v.authMode,
    endpoint: toEndpoint(v),
  };
}

export function toUpdateBody(v: ConnectorFormValues, builtin = false): ConnectorUpdate {
  // An integration's connector (phase 5) has no server settings: only its name and description change.
  if (builtin) return { name: v.name.trim(), description: v.description.trim() || null };
  return {
    name: v.name.trim(),
    description: v.description.trim() || null,
    authMode: v.authMode,
    endpoint: toEndpoint(v),
  };
}
